import { PICQER_MAX_REQUESTS_PER_MINUTE_PER_KEY, HISTORY_WORKERS_PER_KEY } from '../pages/PicqerAnalyse/types';

const WINDOW_MS = 60_000;

class SlotRateLimiter {
  private timestamps: number[] = [];

  constructor(private readonly maxPerMinute: number) {}

  async acquire(): Promise<void> {
    for (;;) {
      const now = Date.now();
      this.timestamps = this.timestamps.filter((t) => now - t < WINDOW_MS);

      if (this.timestamps.length < this.maxPerMinute) {
        this.timestamps.push(now);
        return;
      }

      const waitMs = WINDOW_MS - (now - this.timestamps[0]) + 25;
      await new Promise((r) => setTimeout(r, Math.max(waitMs, 50)));
    }
  }
}

/** Round-robin pool — one rate limiter per Picqer API key slot. */
class PicqerKeyPool {
  private limiters: SlotRateLimiter[] = [new SlotRateLimiter(PICQER_MAX_REQUESTS_PER_MINUTE_PER_KEY)];
  private poolSize = 1;
  private nextSlot = 0;
  private initPromise: Promise<number> | null = null;

  get size(): number {
    return this.poolSize;
  }

  /** Fetch pool size from edge function (once). */
  async ensureInitialized(fetchPoolSize: () => Promise<number>): Promise<number> {
    if (!this.initPromise) {
      this.initPromise = fetchPoolSize().then((size) => {
        const n = Math.max(1, size);
        this.poolSize = n;
        this.limiters = Array.from(
          { length: n },
          () => new SlotRateLimiter(PICQER_MAX_REQUESTS_PER_MINUTE_PER_KEY),
        );
        return n;
      });
    }
    return this.initPromise;
  }

  /** Pin a worker to a slot (spreads load evenly across keys). */
  slotForWorker(workerIndex: number): number {
    return workerIndex % this.poolSize;
  }

  /** Acquire rate-limit token for a slot; waits if that key's budget is exhausted. */
  async acquire(slot?: number): Promise<number> {
    const s = slot ?? this.nextSlot++ % this.poolSize;
    const limiter = this.limiters[s] ?? this.limiters[0];
    await limiter.acquire();
    return s;
  }

  recommendedConcurrency(): number {
    return this.poolSize * HISTORY_WORKERS_PER_KEY;
  }
}

export const picqerKeyPool = new PicqerKeyPool();
